import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Dumbbell, CheckCircle2 } from "lucide-react";

/**
 * Picker dialog used when the user taps "Swap exercise" on a row inside
 * an expanded day card. Lists the global Exercise catalogue, filtered by
 * a search box AND by the target muscle group of the exercise being
 * replaced (so substitutes are biomechanically sensible by default —
 * "Show all" toggles off the muscle-group narrowing).
 */
export default function SwapExerciseDialog({
  open,
  onOpenChange,
  ctx, // { plan, exerciseIdx }
  allExercises,
  onSelect,
}) {
  const original = ctx?.plan?.exercises?.[ctx?.exerciseIdx];
  const fullOriginal = useMemo(() => {
    if (!original) return null;
    return (
      allExercises.find(
        (e) => (e.name || "").trim().toLowerCase() === (original.name || "").trim().toLowerCase(),
      ) || null
    );
  }, [original, allExercises]);

  const targetMuscle = (
    (Array.isArray(fullOriginal?.targetMuscles) ? fullOriginal.targetMuscles[0] : null) ||
    fullOriginal?.target ||
    ""
  ).toLowerCase();
  const [query, setQuery] = useState("");
  const [matchMuscle, setMatchMuscle] = useState(true);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allExercises.filter((e) => {
      const eTarget = (
        (Array.isArray(e.targetMuscles) ? e.targetMuscles[0] : null) ||
        e.target ||
        ""
      ).toLowerCase();
      if (matchMuscle && targetMuscle) {
        if (eTarget !== targetMuscle) return false;
      }
      if (!q) return true;
      const eEquip = (
        (Array.isArray(e.equipments) ? e.equipments.join(" ") : null) ||
        e.equipment ||
        ""
      ).toLowerCase();
      return (
        (e.name || "").toLowerCase().includes(q) ||
        eTarget.includes(q) ||
        eEquip.includes(q)
      );
    }).slice(0, 50); // cap so the list stays performant on phones
  }, [allExercises, query, matchMuscle, targetMuscle]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#0B1220] border-white/10 text-white"
        data-testid="swap-exercise-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">Swap exercise</DialogTitle>
          <DialogDescription className="text-white/60">
            Replacing <span className="capitalize text-white">{original?.name}</span>
            {targetMuscle ? <> · target: <span className="capitalize">{targetMuscle}</span></> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search exercises"
              className="pl-9 bg-white/[0.05] border-white/15 text-white placeholder:text-white/40"
              data-testid="swap-exercise-search"
            />
          </div>
          {targetMuscle && (
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
              <input
                type="checkbox"
                checked={matchMuscle}
                onChange={(e) => setMatchMuscle(e.target.checked)}
                className="accent-sky-500"
                data-testid="swap-exercise-match-muscle"
              />
              Match target muscle ({targetMuscle})
            </label>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto -mr-2 pr-2 space-y-2">
          {filtered.length === 0 && (
            <p className="text-sm text-white/55 py-6 text-center">No matching exercises.</p>
          )}
          {filtered.map((ex) => {
            const isCurrent =
              (ex.name || "").trim().toLowerCase() === (original?.name || "").trim().toLowerCase();
            const gif = ex.gifUrl || ex.gif_url || ex.media || ex.video_url;
            return (
              <button
                type="button"
                key={ex.id || ex.name}
                onClick={() => onSelect(ex)}
                data-testid={`swap-exercise-option-${(ex.name || "").replace(/\s+/g, "-")}`}
                className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition text-left ${
                  isCurrent
                    ? "border-sky-400/60 bg-sky-500/[0.08]"
                    : "border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]"
                }`}
              >
                <div className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-black/50 flex items-center justify-center">
                  {gif ? (
                    <img src={gif} alt={ex.name} className="w-full h-full object-cover" />
                  ) : (
                    <Dumbbell className="w-5 h-5 text-white/30" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold capitalize truncate">{ex.name}</p>
                  <p className="text-[11px] text-white/50 capitalize truncate">
                    {[ex.target, ex.equipment].filter(Boolean).join(" · ")}
                  </p>
                </div>
                {isCurrent && <CheckCircle2 className="w-4 h-4 text-sky-400" />}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
